import useOrganizationSubscriptionItems from 'hooks/use-organization-subscription-items'
import useBillingSession from 'hooks/use-billing-session'
import useGetPartnerToken from 'hooks/use-get-partner-token'
import { Organization } from 'interfaces/organization'
import { User } from 'interfaces/user'
import { useState } from 'react'
import { Card, Badge, Button, Table } from 'react-bootstrap'
import toast from 'react-hot-toast'
import { useHistory } from 'react-router-dom'

interface OrganizationStripeCardProps {
  setShowSubscriptionIdUpdateModal: (show: boolean) => void
  org?: Organization
  orgId: string
  isInHouseTrial: boolean
  internalAdmins?: User[]
}

export const OrganizationStripeCard = ({
  setShowSubscriptionIdUpdateModal,
  org,
  orgId,
  isInHouseTrial,
  internalAdmins,
}: OrganizationStripeCardProps) => {
  const [cachedBillingUrl, setCachedBillingUrl] = useState<string | null>(null)
  const history = useHistory()
  const { data: subscriptionItems } = useOrganizationSubscriptionItems(orgId)
  const { mutate: createBillingSession, isLoading: isBillingSessionLoading } =
    useBillingSession((url) => setCachedBillingUrl(url))
  const { data: tokenData } = useGetPartnerToken(orgId)
  const partnerIntegrations = tokenData?.data || []
  const isSandbox = partnerIntegrations.some((i) => i.tier === 'sandbox')
  const isCli = partnerIntegrations.some((i) => i.tier === 'cli')

  const orgSub = org?.organization_subscription

  // Normalize possible shapes of subscription items (array or { items: [] })
  const itemsArray: any[] = Array.isArray(subscriptionItems)
    ? subscriptionItems
    : (subscriptionItems as any)?.items || []

  if (isSandbox) {
    return (
      <Card
        className="mt-4"
        style={{ backgroundColor: 'rgba(139, 58, 58, 0.15)' }}
      >
        <Card.Header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <p style={{ fontSize: '18px', margin: 0 }}>
            Stripe Subscription Details
          </p>
          <Badge variant="warning">Sandbox</Badge>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-3">
            <h5>Sandbox Organization</h5>
            <p className="text-muted mb-0">
              Stripe billing does not apply to sandbox organizations.
            </p>
          </div>
        </Card.Body>
      </Card>
    )
  }

  if (isCli) {
    return (
      <Card
        className="mt-4"
        style={{ backgroundColor: 'rgba(46, 89, 132, 0.15)' }}
      >
        <Card.Header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <p style={{ fontSize: '18px', margin: 0 }}>
            Stripe Subscription Details
          </p>
          <Badge variant="info">CLI</Badge>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-3">
            <h5>CLI Organization</h5>
            <p className="text-muted mb-0">
              No subscription yet — CLI accounts use a shared line and convert
              to paid via Stripe checkout.
            </p>
          </div>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <Card className="mt-4">
        <Card.Header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <p
            style={{
              fontSize: '18px',
              margin: 0,
            }}
          >
            Stripe Subscription Details
          </p>
          <span>
            {!isInHouseTrial ? (
              <a
                href={`https://dashboard.stripe.com/subscriptions/${org?.organization_subscription?.subscription_id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#4da6ff',
                  fontSize: '17px',
                  textDecoration: 'underline',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#357abd'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#4da6ff'
                }}
                title="Click to open in Stripe"
              >
                {org?.organization_subscription?.subscription_id}
              </a>
            ) : (
              <span style={{ fontSize: '17px' }}>
                {org?.organization_subscription?.subscription_id}
              </span>
            )}{' '}
            <span
              style={{
                cursor: 'pointer',
                color: '#4da6ff',
                fontSize: '17px',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f7ff'
                e.currentTarget.style.color = '#357abd'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = '#4da6ff'
              }}
              onClick={() => setShowSubscriptionIdUpdateModal(true)}
              title="Click to edit subscription ID"
            >
              (edit)
            </span>
          </span>
        </Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive variant="dark">
            <thead>
              <tr>
                <th>Product</th>
                <th>Status</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {itemsArray.length > 0 &&
                itemsArray.map((item: any) => (
                  <tr key={`${item.product_id}-${item.price_id}`}>
                    <td>{item.product_name}</td>
                    <td>{item.status}</td>
                    <td>{item.quantity}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Card.Body>
        {orgSub && (
          <Card.Footer>
            <Button
              variant="primary"
              onClick={() => {
                if (cachedBillingUrl) {
                  navigator.clipboard.writeText(cachedBillingUrl)
                  toast.success('Link copied to clipboard!', {
                    icon: '📋',
                  })
                } else {
                  createBillingSession({ orgId })
                }
              }}
              style={{
                width: '100%',
                marginBottom: '10px',
              }}
              disabled={isBillingSessionLoading}
            >
              {isBillingSessionLoading
                ? 'Creating Stripe Billing Session...'
                : cachedBillingUrl
                ? 'Copy Stripe Billing Session Link'
                : 'Create  Stripe Billing Session Link'}
            </Button>
            <Button
              variant="info"
              onClick={() =>
                history.push(`/organizations/${orgId}/stripe-update`)
              }
              style={{
                width: '100%',
              }}
            >
              Subscription Update
            </Button>
          </Card.Footer>
        )}
      </Card>
    </>
  )
}
